/**
 * The deployments this frontend can be built for.
 *
 * The single place a deployment is named. Every `Flavor`-keyed structure —
 * `TAGS_ENABLED_BY_FLAVOR` in `Helpers/tags.ts`, `COMING_SOON_BY_FLAVOR`,
 * `PERMANENT_BY_FLAVOR` — imports it from here, so adding a deployment is a
 * missing-key error in all of them at once until someone decides what it gets.
 *
 * That error is real but nothing surfaces it today. `tsc` 4.9.5 cannot parse
 * the `.d.ts` syntax in `@base-ui/utils` and `@types/d3-dispatch`, and
 * syntactic errors suppress semantic diagnostics program-wide, so
 * `npx tsc --noEmit` reports nothing about `src/` whatever it contains —
 * checked in isolation, the missing key is a `TS2741`. Until the toolchain is
 * fixed this is a reviewer's aid, not a gate.
 */
export type Flavor = 'bops' | 'gs1mex' | 'logyca' | 'logyca-bops';

const flavor = process.env.REACT_APP_FLAVOR || 'bops';

/**
 * The flavors that render the Logyca product.
 *
 * Two deployments do: `logyca` is Logyca's own environment
 * (api.logycaco.bops.ai), `logyca-bops` the older one still served by the bops
 * core_api (api.bopsmetrics.com, and api.dev-bops.com for its dev twin). The UI
 * is the same on both, so every `isLogycaFlavor` consumer covers both.
 *
 * They are separate flavors because the backends behind them grant
 * entitlements differently — see `Helpers/tags.ts`, which is what the split
 * exists to get right.
 *
 * Typed against `Flavor` so a typo in this list — `logica-bops`, or a stale
 * `logyca-prod` — is a compile error rather than a deployment that silently
 * loses its auth guard, sidebar and suppliers routes. The cast on the lookup
 * below is the untyped env var, not this list.
 */
const LOGYCA_FLAVORS: readonly Flavor[] = ['logyca', 'logyca-bops'];

const config = {
  REACT_APP_AUTH0_AUDIENCE: process.env.REACT_APP_AUTH0_AUDIENCE || '',
  REACT_APP_AUTH0_DOMAIN: process.env.REACT_APP_AUTH0_DOMAIN,
  REACT_APP_AUTH0_CALLBACK_URL: process.env.REACT_APP_AUTH0_CALLBACK_URL,
  REACT_APP_AUTH0_CLIENT_ID: process.env.REACT_APP_AUTH0_CLIENT_ID,
  REACT_APP_BASE_API_ADDRESS: process.env.REACT_APP_BASE_API_ADDRESS || '',
  REACT_APP_OPEN_AI_PROMPT: process.env.REACT_APP_OPEN_AI_PROMPT || '',
  REACT_APP_FLAVOR: flavor,
  isGs1Flavor: flavor === 'gs1mex',
  isLogycaFlavor: LOGYCA_FLAVORS.includes(flavor as Flavor),
  REACT_APP_LOGYCA_LOGIN_URL: process.env.REACT_APP_LOGYCA_LOGIN_URL || '',
  REACT_APP_API_TIMEOUT_MS: process.env.REACT_APP_API_TIMEOUT_MS || '120000',
  // Local-only Logyca login bypass — set in `.env.local` (gitignored, never
  // present in a deployed build) to skip the redirect to the external
  // Logyca SSO login page when developing against it locally.
  REACT_APP_DEV_TOKEN: process.env.REACT_APP_DEV_TOKEN || '',
  REACT_APP_DEV_EMAIL: process.env.REACT_APP_DEV_EMAIL || '',
  REACT_APP_DEV_REFRESH: process.env.REACT_APP_DEV_REFRESH || '',
};

export default config;
